// Logo upload flow shared by the org-detail panel and the bulk upload
// (ADR-0033 brand logos, ADR-0164 org logo). No React here: the screens
// own their state, this module owns the wire.
//
// Three steps on the wire for either target:
//   1. POST …/logo/upload-url  → presign (core-api)
//   2. PUT bytes directly to R2 / MinIO — the signature is in the query
//      string; requiredHeaders are replayed verbatim, no Authorization.
//   3. POST …/logo/commit      → HEAD + persist (core-api)
//
// Every failure surfaces as an Error with a fixed, user-facing es-CL
// message, so callers render `err.message` without mapping codes.

import { adminApi } from "../../lib/api/endpoints/admin";
import { ApiError } from "../../lib/api/error";
import { padToSquare } from "../../lib/media/padToSquare";

export const LOGO_ALLOWED_MIME = ["image/png", "image/jpeg", "image/webp"] as const;
export const LOGO_MAX_BYTES = 2 * 1024 * 1024;
export const LOGO_ACCEPT_ATTR = LOGO_ALLOWED_MIME.join(",");

export type LogoTarget = { kind: "org" } | { kind: "brand"; brandId: string };

export const LOGO_INVALID_FILE_MESSAGE =
  "Este archivo no sirve como logo. Usa PNG, JPG o WEBP de hasta 2 MB.";
export const LOGO_PUT_FAILED_MESSAGE =
  "No pudimos subir el logo. Revisa tu conexión e inténtalo de nuevo.";
export const LOGO_SESSION_EXPIRED_MESSAGE = "Tu sesión venció. Vuelve a iniciar sesión.";

type AllowedMime = (typeof LOGO_ALLOWED_MIME)[number];

function isAllowedMime(type: string): type is AllowedMime {
  return (LOGO_ALLOWED_MIME as readonly string[]).includes(type);
}

/** Client-side guard before any round-trip. The server enforces both
 *  checks again; this is fast feedback only. */
export function validateLogoFile(file: File): string | null {
  if (!isAllowedMime(file.type)) return LOGO_INVALID_FILE_MESSAGE;
  if (file.size <= 0 || file.size > LOGO_MAX_BYTES) return LOGO_INVALID_FILE_MESSAGE;
  return null;
}

function isAuthFailure(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 401 || err.status === 403);
}

export async function uploadLogoFile(
  orgId: string,
  target: LogoTarget,
  file: File
): Promise<{ logoUrl: string }> {
  const invalid = validateLogoFile(file);
  if (invalid) throw new Error(invalid);

  // Pad a non-square logo to a square canvas so it never renders
  // trimmed. The presign signs contentType + contentLength, so both come
  // from the padded blob — not the source File.
  const { blob, mimeType } = await padToSquare(file);
  if (blob.size > LOGO_MAX_BYTES) throw new Error(LOGO_INVALID_FILE_MESSAGE);

  try {
    const presignBody = { contentType: mimeType, contentLength: blob.size };
    const presign =
      target.kind === "org"
        ? await adminApi.requestOrgLogoUploadUrl(orgId, presignBody)
        : await adminApi.requestBrandLogoUploadUrl(orgId, target.brandId, presignBody);

    let putOk = false;
    try {
      const putRes = await fetch(presign.uploadUrl, {
        method: "PUT",
        body: blob,
        headers: presign.requiredHeaders,
      });
      putOk = putRes.ok;
    } catch {
      putOk = false;
    }
    if (!putOk) throw new Error(LOGO_PUT_FAILED_MESSAGE);

    if (target.kind === "org") {
      const res = await adminApi.commitOrgLogo(orgId, { objectKey: presign.objectKey });
      return { logoUrl: res.organization.logoUrl };
    }
    const res = await adminApi.commitBrandLogo(orgId, target.brandId, {
      objectKey: presign.objectKey,
    });
    return { logoUrl: res.brand.logoUrl ?? presign.publicUrl };
  } catch (err) {
    if (isAuthFailure(err)) throw new Error(LOGO_SESSION_EXPIRED_MESSAGE);
    throw err;
  }
}
